import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, StoreIcon, PlusIcon, SearchIcon, QrCodeIcon, SendIcon, ChevronDownIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
import { TransactionReceipt } from '../TransactionReceipt';
interface RaastMerchantPaymentsProps {
  onBack: () => void;
  onHome: () => void;
  onTransfer: () => void;
}
type Step = 'home' | 'list' | 'add' | 'review' | 'otp' | 'success';
export function RaastMerchantPayments({
  onBack,
  onHome,
  onTransfer
}: RaastMerchantPaymentsProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('home');
  const [direction, setDirection] = useState(1);
  const [payeeId, setPayeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedTag, setSelectedTag] = useState('Groceries');
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const tags = ['Groceries', 'Utilities', 'Dining', 'Shopping', 'Other'];
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`merchant-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`merchant-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2379" data-mp="1pa1lu7tcn4cf">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2380" data-mp="1kct2w50dmwa4">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2381" data-mp="1am868n49k1tu" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2382" data-mp="281caeiblr515">{title}</h1>
      <button onClick={onHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-2383" data-mp="282ngwpwfmqvy">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2384" data-mp="2amk29v2bzcgu" />
      </button>
    </div>;
  const renderHome = () => <motion.div key="home" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2385" data-mp="2flwms95qc7g9">
      {renderHeader('Pay to Merchant', onBack)}
      <div className="px-6 flex-1 flex flex-col gap-4 pb-8 mt-2" data-id="element-2386" data-mp="2tkwexqsgb4h6">
        <button onClick={() => navigateTo('list')} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2387" data-mp="2tijwlcis6s3l">
          <div className="flex items-center justify-between" data-id="element-2388" data-mp="33civ81jnbtqs">
            <div data-id="element-2389" data-mp="388hexbu14nkv">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2390" data-mp="388zlp2ebik71">
                Saved Merchants
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2391" data-mp="3d6izy2b0mwyz">
                Pay your saved merchants
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2392" data-mp="it63vcklr7tu">
              <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2393" data-mp="dsifyzro773u" />
            </div>
          </div>
        </button>
        <button onClick={() => navigateTo('add')} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2394" data-mp="rnu9x4y04tjp">
          <div className="flex items-center justify-between" data-id="element-2395" data-mp="3w2byh7lzl7pe">
            <div data-id="element-2396" data-mp="3r2g3tyz6ismi">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2397" data-mp="3r2zagjjdpul8">
                Add New Payee
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2398" data-mp="8t5htivcmdly">Add a new merchant ID</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2399" data-mp="3yjdfznoplbd">
              <PlusIcon className="w-6 h-6 text-[#F37024]" data-id="element-2400" data-mp="1fasm3qf39fbt" />
            </div>
          </div>
        </button>
        <button onClick={onTransfer} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2401" data-mp="1nr1clfzpyn6o">
          <div className="flex items-center justify-between" data-id="element-2402" data-mp="1dvghm1t2in6f">
            <div data-id="element-2403" data-mp="1iti0omqhumhw">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2404" data-mp="1iszvh0lusxxu">
                RAAST Transfer
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2405" data-mp="26k5ajdjyzm73">Send money to a person</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2406" data-mp="2bh5gzpkqeqyq">
              <SendIcon className="w-6 h-6 text-[#F37024]" data-id="element-2407" data-mp="21sds5qaywms1" />
            </div>
          </div>
        </button>
      </div>
    </motion.div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2408" data-mp="2pgnagi8z2sef">
      {renderHeader('Saved Merchants', () => navigateTo('home', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2409" data-mp="2z6de9x3wwqki">
        <div className="relative mb-6" data-id="element-2410" data-mp="2u5y053bt3f1h">
          <SearchIcon className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textSec}`} data-id="element-2411" data-mp="2u4o1abx4q2ts" />
          <input type="text" placeholder="Search merchants..." className={`w-full rounded-2xl py-3 pl-12 pr-4 text-[14px] outline-none border ${inputBg} ${textPri} ${borderClr}`} data-id="element-2412" data-mp="2q78git06d19e" />
        </div>
        <div className="space-y-4" data-id="element-2413" data-mp="2bf80dnhj88cu">
          {['Al-Fatah', 'Imtiaz Super Market', 'KFC'].map((merchant, i) => <button key={i} onClick={() => {
          setPayeeId(`M-${Math.floor(Math.random() * 10000)}`);
          navigateTo('add');
        }} className={`w-full flex items-center gap-4 p-4 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2414" data-mp="26hy53r7gp2h3" data-mpi={i}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2415" data-mp="cxo0mu3x5m6n">
                <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2416" data-mp="lym7dk5qs7sx" />
              </div>
              <div className="text-left flex-1" data-id="element-2417" data-mp="qxozy54jclk0">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2418" data-mp="qxfb4kv88kfw">{merchant}</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-2419" data-mp="3qe3sbr669yjq">
                  Merchant ID: M-{1000 + i}
                </p>
              </div>
            </button>)}
        </div>
      </div>
    </motion.div>;
  const renderAdd = () => <motion.div key="add" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2420" data-mp="36jm1vi5o3cc">
      {renderHeader('Add Payee Details', () => navigateTo('home', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2421" data-mp="3bm3amh7vsxl2">
        <div className="space-y-6 mb-auto" data-id="element-2422" data-mp="251wuxr0dmsoz">
          <div data-id="element-2423" data-mp="252ot99lcz4iy">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2424" data-mp="201r3aau3x66w">
              Merchant ID / Till Number
            </label>
            <input type="text" value={payeeId} onChange={e => setPayeeId(e.target.value)} placeholder="Enter ID" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2425" data-mp="2e2l8kkluyr1c" />
          </div>

          <div className="relative z-20" data-id="element-2426" data-mp="2xpj8ns3bjdpk">
            <h3 className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2427" data-mp="2xo7vx2rwavmb">
              Pay From
            </h3>
            <button onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between border shadow-sm ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2428" data-mp="2sqy9lvzsz02p">
              <div className="text-left" data-id="element-2429" data-mp="3ldftzciwgz95">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2430" data-mp="3qbr4lph0rdsf">
                  {payFromAccount.label}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2431" data-mp="3cb6kmpzdbbxc">
                  {payFromAccount.number}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2432" data-mp="3gc0c1tc0r8uz" />
            </button>
            <p className={`text-[12px] mt-2 ml-2 ${textSec}`} data-id="element-2433" data-mp="32ku55c6eu8y2">
              Available Balance: PKR {payFromAccount.balance.toLocaleString()}
              .00
            </p>

            <AnimatePresence data-id="element-2434" data-mp="2spa7ak8ad06o">
              {showPayFromDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-[80px] left-0 right-0 rounded-2xl shadow-xl overflow-hidden border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.2)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2435" data-mp="2xn28y5yqg0o2">
                  {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                setPayFromAccount(acc);
                setShowPayFromDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${isDark ? 'border-[rgba(243,112,36,0.1)] hover:bg-white/5' : 'border-[#F0E8E3] hover:bg-black/5'}`} data-id="element-2436" data-mp="utkkchj5lc6r">
                      <p className={`font-bold ${textPri}`} data-id="element-2437" data-mp="1dpkiyb6mzdzb">{acc.label}</p>
                      <p className={`text-sm ${textSec}`} data-id="element-2438" data-mp="21g7qgvcn41lc">{acc.number}</p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          <div data-id="element-2439" data-mp="2fkym1t3prnh7">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2440" data-mp="2fkf3qba5ix2n">
              Amount (PKR)
            </label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2441" data-mp="2pcbcqhds07ln" />
          </div>
          <div data-id="element-2442" data-mp="1sndgeuqdo1md">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2443" data-mp="1snvbho1b09sp">
              Tag
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-2444" data-mp="26jgyjpnz8jih">
              {tags.map(tag => <button key={tag} onClick={() => setSelectedTag(tag)} className={`px-4 py-2 rounded-full text-[13px] font-bold transition-colors border ${selectedTag === tag ? 'bg-[#F37024] text-white border-[#F37024]' : `${cardBg} ${textSec} ${borderClr}`}`} data-id="element-2445" data-mp="21oti8e34mvez">
                  {tag}
                </button>)}
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('review')} disabled={!payeeId || !amount} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2446" data-mp="3u2rtqwtuyyqi">
          Next
        </button>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2447" data-mp="36e8ic8fii5u7">
      {renderHeader('Review Payment', () => navigateTo('add', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2448" data-mp="2inbi3sx2wwys">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2449" data-mp="2nmxwi5on8j96">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2450" data-mp="2g3ycpyx9sppa">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2451" data-mp="2b4lphd2hlgm6">
              <StoreIcon className="w-8 h-8 text-[#F37024]" data-id="element-2452" data-mp="263nxy9vl7a80" />
            </div>
            <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2453" data-mp="215mfnr5zjexl">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2454" data-mp="1x6nemhndk2bf">
              To Merchant ID: {payeeId}
            </p>
          </div>
          <div className="space-y-4" data-id="element-2455" data-mp="1nehnfa4y5oxa">
            <div className="flex justify-between" data-id="element-2456" data-mp="1nfiz9gtsefg2">
              <span className={textSec} data-id="element-2457" data-mp="1ig6fj4y7pohp">Pay From</span>
              <span className={`font-bold ${textPri}`} data-id="element-2458" data-mp="1ihr8wr8sj1dz">
                {payFromAccount.number.slice(-4)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2459" data-mp="19fi0c6x31i38">
              <span className={textSec} data-id="element-2460" data-mp="19frnlmqqtb58">Tag</span>
              <span className={`font-bold ${textPri}`} data-id="element-2461" data-mp="1y65p99rfbelc">{selectedTag}</span>
            </div>
            <div className="flex justify-between" data-id="element-2462" data-mp="234ph1qsp7klm">
              <span className={textSec} data-id="element-2463" data-mp="234h3rxsnyd9w">Fee</span>
              <span className={`font-bold ${textPri}`} data-id="element-2464" data-mp="1pdu15ujs838j">Rs. 0</span>
            </div>
            <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2465" data-mp="1tf5w8flfdgo4" />
            <div className="flex justify-between" data-id="element-2466" data-mp="1tdmgqr7lf9ne">
              <span className={`font-bold ${textPri}`} data-id="element-2467" data-mp="1ffo5vvshdvcn">Total Amount</span>
              <span className={`font-bold text-[#F37024]`} data-id="element-2468" data-mp="1fhr3b0vyqqre">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2469" data-mp="16gsxqe5vmb6p">
          Confirm Payment
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2470" data-mp="34tsw244e22qu">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2471" data-mp="12xkdp0m0c83h">
        <div className="mb-8 text-center" data-id="element-2472" data-mp="y6c2q0514eo7">
          <p className={`text-[14px] ${textSec}`} data-id="element-2473" data-mp="y5afkxaqo1q4">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2474" data-mp="17vvl74oty6ij">
          {otp.map((digit, i) => <input key={i} id={`merchant-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2475" data-mp="1lvo8eq15e4lr" data-mpi={i} />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2476" data-mp="1wj7ugl67xvea">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2477" data-mp="1wl2dftxxjdfq">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2478" data-mp="25iuw982v137v">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <TransactionReceipt amount={parseInt(amount || '0')} toName={`Merchant ${payeeId}`} toAccount={payeeId} fromName={payFromAccount.holder} fromAccount={payFromAccount.number} additionalInfo={[{
    label: 'Channel',
    value: 'RAAST'
  }, {
    label: 'Tag',
    value: selectedTag
  }, {
    label: 'Transaction Fee',
    value: 'PKR 0'
  }]} onNavigateHome={onHome} data-id="element-2479" data-mp="2k924yw32f7vd" />;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2480" data-mp="8m01dlj35p0p">
      {step === 'home' && renderHome()}
      {step === 'list' && renderList()}
      {step === 'add' && renderAdd()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}